import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, BookOpen, Compass, Zap, ShoppingBag, CreditCard, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { useTour } from '../context/TourContext';

const QUICK_STEPS = [
  { step: 1, text: 'Sign up or sign in', link: '/auth' },
  { step: 2, text: 'Create a design (Generate page) or pick a Ready-Made design', link: '/design' },
  { step: 3, text: 'Choose a product (e.g. T-shirt, size, color)', link: '/products' },
  { step: 4, text: 'Configure mockup & shipping, then generate mockup', link: '/configure-mockup' },
  { step: 5, text: 'Review mockup → Proceed to Payment → Complete purchase', link: '/mockups' },
];

const FLOWS = [
  {
    title: 'Create your own design → Order',
    steps: 'Generate → Use image → Products → Configure mockup → Review → Payment',
    link: '/design',
  },
  {
    title: 'Use a Ready-Made design',
    steps: 'Ready Made → Buy Now → Products → Configure mockup → Review → Payment',
    link: '/designs',
  },
  {
    title: 'Start from your Gallery',
    steps: 'Gallery → Click image → Products (or Configure if product already chosen) → Review → Payment',
    link: '/gallery',
  },
];

const TOUR_OPTIONS = [
  { name: 'Landing & navigation', tour: 'landing', path: '/' },
  { name: 'Design / Generate page', tour: 'design', path: '/design' },
  { name: 'Gallery', tour: 'gallery', path: '/gallery' },
  { name: 'Products catalog', tour: 'products', path: '/products' },
  { name: 'Mockups', tour: 'mockups', path: '/mockups' },
];

export default function HelpPage() {
  const { startTour } = useTour();
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (id) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-10 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4">
            <HelpCircle size={32} />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Help Center</h1>
          <p className="text-lg text-gray-600">
            Quick start, guided tours, and the user manual.
          </p>
        </div>

        {/* Quick Start */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Zap size={22} className="text-primary" />
            Quick start
          </h2>
          <ol className="space-y-3">
            {QUICK_STEPS.map(({ step, text, link }) => (
              <li key={step} className="flex items-center gap-3">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white font-bold flex items-center justify-center text-sm">
                  {step}
                </span>
                <span className="text-gray-700">{text}</span>
                <Link to={link} className="text-primary font-semibold text-sm whitespace-nowrap hover:underline">
                  Go →
                </Link>
              </li>
            ))}
          </ol>
        </section>

        {/* Take the tour */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Compass size={22} className="text-primary" />
            Take a guided tour
          </h2>
          <p className="text-gray-600 mb-4">
            We&apos;ll take you to the page and highlight the main areas. Pick a tour to start:
          </p>
          <div className="flex flex-wrap gap-3">
            {TOUR_OPTIONS.map(({ name, tour, path }) => (
              <button
                key={tour}
                onClick={() => startTour(tour)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 text-primary font-semibold hover:bg-primary/20 transition-colors"
              >
                <Compass size={18} />
                {name}
              </button>
            ))}
          </div>
        </section>

        {/* How do I... accordion */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
          <h2 className="text-xl font-bold text-gray-900 p-6 pb-0">How do I…?</h2>
          <div className="divide-y divide-gray-100">
            {[
              {
                id: 'create-design',
                q: 'Create a design (AI or upload)?',
                body: (
                  <>
                    Go to <Link to="/design" className="text-primary font-semibold hover:underline">Generate</Link>.
                    Enter a text prompt and choose a style, then click Generate (uses credits). Or upload your own image — no credits needed. When the image appears, click &quot;Use This Image&quot; to pick a product.
                  </>
                ),
              },
              {
                id: 'order-tshirt',
                q: 'Order a custom T-shirt?',
                body: (
                  <>
                    Create or select a design → go to <Link to="/products" className="text-primary font-semibold hover:underline">Products</Link> and choose a product (e.g. T-shirt, size, color) → you&apos;ll land on Configure Mockup. Fill shipping details and click Generate Mockup. When the mockup is ready, click Proceed to Payment on the mockup card (Mockups page) or from the configure step, then complete payment.
                  </>
                ),
              },
              {
                id: 'buy-credits',
                q: 'Buy credits for AI generation?',
                body: (
                  <>
                    Click the <strong>credits counter</strong> (coin icon) in the top navbar. Choose a package and pay with your card. Credits are used only for AI image generation, not for uploads or mockups.
                  </>
                ),
              },
              {
                id: 'view-orders',
                q: 'View my orders?',
                body: (
                  <>
                    Go to <Link to="/orders" className="text-primary font-semibold hover:underline">Order History</Link> in the navbar. Click &quot;View Details&quot; on any order for full info and status.
                  </>
                ),
              },
              {
                id: 'ready-made',
                q: 'Use a Ready-Made design?',
                body: (
                  <>
                    Go to <Link to="/designs" className="text-primary font-semibold hover:underline">Ready Made</Link>. Click &quot;Buy Now&quot; or &quot;Customize&quot; on a design — you&apos;ll go to Products with that design selected. Pick a product and follow the same mockup → payment flow.
                  </>
                ),
              },
            ].map(({ id, q, body }) => (
              <div key={id}>
                <button
                  onClick={() => toggleSection(id)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{q}</span>
                  {openSection === id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openSection === id && (
                  <div className="px-4 pb-4 text-gray-600 text-sm border-t border-gray-100 pt-2">
                    {body}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Flows summary */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ShoppingBag size={22} className="text-primary" />
            Main flows
          </h2>
          <ul className="space-y-4">
            {FLOWS.map((flow, i) => (
              <li key={i} className="border-l-4 border-primary/30 pl-4 py-1">
                <p className="font-semibold text-gray-900">{flow.title}</p>
                <p className="text-sm text-gray-600 mt-1">{flow.steps}</p>
                <Link to={flow.link} className="text-primary font-semibold text-sm mt-2 inline-block hover:underline">
                  Start: {flow.link} →
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* User manual CTA */}
        <section className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
          <BookOpen size={40} className="text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">User manual</h2>
          <p className="text-gray-600 mb-6 max-w-lg mx-auto">
            Step-by-step guide for every feature: account, design, gallery, products, mockups, payment, orders, profile, credits, and admin.
          </p>
          <Link
            to="/help/manual"
            className="inline-flex items-center gap-2 bg-primary hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-colors"
          >
            <BookOpen size={20} />
            Open user manual
          </Link>
        </section>

        {/* Legal */}
        <p className="text-center text-sm text-gray-500 mt-8">
          <Link to="/terms" className="hover:underline">Terms</Link>
          {' · '}
          <Link to="/privacy" className="hover:underline">Privacy</Link>
          {' · '}
          <Link to="/refund-policy" className="hover:underline">Refund policy</Link>
        </p>
      </div>
    </div>
  );
}
